import { EmptyState } from '@components/EmptyState';
import { IconProfileOff } from '@components/icons';
import { ProfileCard } from '@components/ProfileCard';
import { WhatsappCard } from '@components/WhatsappCard';
import type { ApolloEventDoc, ApolloSequence, Lead, SequenceActionStatus } from '@services/firebase/firestore';
import { AddMenu } from './AddMenu';
import { buildTransferLinkUrl, deriveContactState, getWhatsAppFromLead } from './utils';
import styles from './ManageLeadsPage.module.scss';

export interface ProfilesSectionProps {
  lead: Lead;
  selectedSiteId: string;
  apolloEventsMap: Map<string, ApolloEventDoc>;
  contactedPersonIds: Set<string>;
  isAddMenuOpen: boolean;
  onAddMenuOpenChange: (open: boolean) => void;
  sequences: ApolloSequence[];
  reachOutStates: Map<string, { status: SequenceActionStatus; message?: string }>;
  onAddContact: () => void;
  onAddWhatsApp: () => void;
  onViewJson: (name: string, data: Record<string, unknown>) => void;
  onEnrichProfile: (personId: string) => void;
  onAddToContacts: (personId: string) => void;
  onReachOutWithSequence: (personId: string, sequenceId: string) => void;
  onReachOutWhatsApp: () => void;
  onEditWhatsApp: () => void;
  onDeleteContact: (personId: string, name: string) => void;
}

export const ProfilesSection = ({
  lead,
  selectedSiteId,
  apolloEventsMap,
  contactedPersonIds,
  isAddMenuOpen,
  onAddMenuOpenChange,
  sequences,
  reachOutStates,
  onAddContact,
  onAddWhatsApp,
  onViewJson,
  onEnrichProfile,
  onAddToContacts,
  onReachOutWithSequence,
  onReachOutWhatsApp,
  onEditWhatsApp,
  onDeleteContact,
}: ProfilesSectionProps) => {
  const whatsApp = getWhatsAppFromLead(lead);

  // WhatsApp is never "contacted" yet (no Firestore persistence)
  const isWhatsAppContacted = false;

  const getReachOutState = (personId: string) =>
    reachOutStates.get(`${selectedSiteId}-${personId}`) ?? null;

  /**
   * Build the personalised transfer-link URL for an enriched person.
   * Returns undefined when any required field is missing — the ProfileCard
   * then omits the copy-link button entirely.
   */
  const getTransferLinkUrl = (person: {
    first_name?: string;
    email?: string;
  }): string | undefined => {
    if (
      !lead.site_id ||
      !lead.site_url ||
      !lead.transfer_code ||
      !lead.onboarded_by ||
      !person.first_name ||
      !person.email
    ) {
      return undefined;
    }
    return (
      buildTransferLinkUrl({
        siteId: lead.site_id,
        firstName: person.first_name,
        email: person.email,
        onboardedBy: lead.onboarded_by,
        siteUrl: lead.site_url,
        transferCode: lead.transfer_code,
      }) ?? undefined
    );
  };

  const hasContactedProfiles =
    isWhatsAppContacted ||
    !!lead.contacts?.apollo?.people_enriched?.some((p) => contactedPersonIds.has(p.id));

  const hasAvailableToContact =
    (whatsApp && !isWhatsAppContacted) ||
    !whatsApp ||
    lead.contacts?.apollo?.people?.some((p) => !contactedPersonIds.has(p.id)) ||
    lead.contacts?.apollo?.people_enriched?.some((p) => !contactedPersonIds.has(p.id));

  const hasNoPeople =
    !whatsApp &&
    (!lead.contacts?.apollo?.people || lead.contacts.apollo.people.length === 0) &&
    (!lead.contacts?.apollo?.people_enriched ||
      lead.contacts.apollo.people_enriched.length === 0);

  return (
    <div className={styles.profilesColumn}>
      {/* Contacted Section */}
      {hasContactedProfiles && (
        <>
          <h3 className={styles.sectionTitle}>Contacted</h3>

          {/* WhatsApp Card (if contacted) */}
          {whatsApp && isWhatsAppContacted && (
            <WhatsappCard
              name={whatsApp.number}
              contacted={true}
              onAction={onReachOutWhatsApp}
              onEdit={onEditWhatsApp}
            />
          )}

          {/* Contacted Profile Cards from people_enriched */}
          {lead.contacts?.apollo?.people_enriched
            ?.filter((person) => contactedPersonIds.has(person.id))
            .map((person) => {
              const cId = lead.person_id_to_contact_id?.[person.id];
              const { contactStatus, contactProgress, sequenceLog } = deriveContactState(
                cId,
                apolloEventsMap
              );
              return (
                <ProfileCard
                  key={person.id}
                  name={person.name}
                  enrichedName={person.name}
                  jobTitle={person.headline || person.title || 'No title available'}
                  email={person.email}
                  avatarSrc={person.photo_url}
                  enriched={true}
                  contacted={true}
                  emailVerified={!!person.email}
                  hasLinkedIn={!!person.linkedin_url}
                  linkedInUrl={person.linkedin_url}
                  personId={person.id}
                  contactId={cId}
                  onViewJson={() => onViewJson(person.name, person as unknown as Record<string, unknown>)}
                  contactStatus={contactStatus}
                  contactProgress={contactProgress}
                  sequenceLog={sequenceLog}
                  transferLinkUrl={getTransferLinkUrl(person)}
                />
              );
            })}
        </>
      )}

      {/* Available to Contact Section */}
      {hasAvailableToContact && (
        <>
          <div
            className={`${styles.sectionTitleRow} ${
              hasContactedProfiles ? styles.sectionTitleWithGap : ''
            }`}
          >
            <h3 className={styles.sectionTitle}>Available to Contact</h3>
            <AddMenu
              isOpen={isAddMenuOpen}
              onOpenChange={onAddMenuOpenChange}
              onAddContact={onAddContact}
              showWhatsApp={!whatsApp}
              onAddWhatsApp={onAddWhatsApp}
            />
          </div>

          {/* WhatsApp Card (if not contacted) */}
          {whatsApp && !isWhatsAppContacted && (
            <WhatsappCard
              name={whatsApp.number}
              contacted={false}
              onAction={onReachOutWhatsApp}
              onEdit={onEditWhatsApp}
            />
          )}

          {/* Enriched ProfileCards not yet contacted (from people_enriched) */}
          {lead.contacts?.apollo?.people_enriched
            ?.filter((person) => !contactedPersonIds.has(person.id))
            .map((person) => (
              <ProfileCard
                key={person.id}
                name={person.name}
                jobTitle={person.headline || person.title || 'No title available'}
                email={person.email}
                avatarSrc={person.photo_url}
                enriched={true}
                emailVerified={!!person.email}
                hasLinkedIn={!!person.linkedin_url}
                linkedInUrl={person.linkedin_url}
                personId={person.id}
                contactId={lead.person_id_to_contact_id?.[person.id]}
                onAddToContacts={() => onAddToContacts(person.id)}
                sequences={sequences}
                onReachOutWithSequence={(seqId) => onReachOutWithSequence(person.id, seqId)}
                reachOutPending={getReachOutState(person.id)?.status === 'pending'}
                reachOutResult={(() => {
                  const state = getReachOutState(person.id);
                  if (!state) return null;
                  if (state.status === 'completed') return 'success' as const;
                  if (state.status === 'failed') return 'failed' as const;
                  if (state.status === 'skipped') return 'skipped' as const;
                  return null;
                })()}
                reachOutMessage={getReachOutState(person.id)?.message}
                onViewJson={() =>
                  onViewJson(person.name, person as unknown as Record<string, unknown>)
                }
                onDeleteContact={
                  lead.person_id_to_contact_id?.[person.id] === undefined
                    ? () => onDeleteContact(person.id, person.name)
                    : undefined
                }
                transferLinkUrl={getTransferLinkUrl(person)}
              />
            ))}

          {/* Non-enriched ProfileCards (from people array) */}
          {lead.contacts?.apollo?.people
            ?.filter((person) => !contactedPersonIds.has(person.id))
            .map((person) => {
              const isInPeopleEnriched = lead.contacts?.apollo?.people_enriched?.some(
                (p) => p.id === person.id
              );
              if (isInPeopleEnriched) return null;

              const isEnriching = person.enrich === true;
              return (
                <ProfileCard
                  key={person.id}
                  name={`${person.first_name} ${person.last_name_obfuscated}`}
                  jobTitle={person.title || 'No title available'}
                  enriched={false}
                  onEnrich={isEnriching ? undefined : () => onEnrichProfile(person.id)}
                />
              );
            })}
        </>
      )}

      {/* Empty state when no contacts at all */}
      {hasNoPeople && (
        <div className={styles.emptyProfilesState}>
          <EmptyState
            icon={<IconProfileOff size={82} color="var(--color-primary-light)" />}
            title="No contacts found"
            subtitle="We haven't found any contacts for this lead yet."
            hidePrimaryActionLeadingIcon
          />
        </div>
      )}
    </div>
  );
};
